import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useEffect, useState } from "react";
import { DateFilterField } from "./DateFilterField";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import {
  DdlListResult,
  DdlResult,
  Operator,
} from "../../../../services/api/types/reports/reports";
import { gray } from "../../../../theme/themePrimitives";
import { DateOperator } from "../../../../services/api/types/users/users";

interface AssignmentFiltersProps {
  value: {
    assignments: string[];
    assignmentsStatus: string[];
    progressStatus: string[];
    dateFilters: {
      attribute: string;
      operator: Operator | "";
      date: string;
      startDate: string;
      endDate: string;
    }[];
  };
  assignmentsOptions: DdlResult | null;
  assignmentsStatusOptions: DdlResult | null;
  progressStatusOptions: DdlResult | null;
  isExpanded: boolean;
  onChange: (data: {
    assignments?: string[];
    assignmentsStatus?: string[];
    progressStatus?: string[];
    dateFilters?: {
      attribute: string;
      operator: Operator | "";
      date: string;
      startDate: string;
      endDate: string;
    }[];
  }) => void;
}

export const AssignmentFilters: React.FC<AssignmentFiltersProps> = ({
  value,
  assignmentsOptions,
  assignmentsStatusOptions,
  progressStatusOptions,
  isExpanded,
  onChange,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [dateFilters, setDateFilters] = useState<
    Array<{
      attribute: string;
      operator: Operator | "";
      startDate: string;
      endDate: string;
      date: string;
    }>
  >([{ attribute: "", operator: "", startDate: "", endDate: "", date: "" }]);

  useEffect(() => {
    setExpanded(isExpanded);
  }, [isExpanded]);

  // Sync local state with parent prop when value changes (for prepopulation)
  useEffect(() => {
    if (
      dateFilters.length === 1 &&
      !dateFilters[0].attribute &&
      value.dateFilters.length > 0
    ) {
      setDateFilters(value.dateFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.dateFilters]);

  const handleAttributeChange = (index: number, attribute: string) => {
    const updated = [...dateFilters];
    updated[index] = {
      ...updated[index],
      attribute,
      operator: "",
      startDate: "",
      endDate: "",
      date: "",
    };
    setDateFilters(updated);
    updateParentState(updated);
  };

  const handleOperatorChange = (index: number, operator: Operator | "") => {
    const updated = [...dateFilters];
    updated[index] = {
      ...updated[index],
      operator,
    };
    setDateFilters(updated);
    updateParentState(updated);
  };
  const handleDateChange = (
    index: number,
    start?: string,
    end?: string,
    single?: string,
  ) => {
    const cloned = [...dateFilters];
    const isBetween = cloned[index].operator === DateOperator.BETWEEN;
    if (isBetween) {
      cloned[index].startDate = start ?? cloned[index].startDate;
      cloned[index].endDate = end ?? cloned[index].endDate;
      cloned[index].date = "";
    } else {
      cloned[index].date = single ?? cloned[index].date;
      cloned[index].startDate = "";
      cloned[index].endDate = "";
    }
    setDateFilters(cloned);
    updateParentState(cloned);
  };

  const updateParentState = (filters: typeof dateFilters) => {
    const mapped = filters
      .filter((f) => f.attribute && f.operator)
      .map((f) => ({
        attribute: f.attribute,
        operator: f.operator,
        date: f.date ?? "",
        startDate: f.startDate ?? "",
        endDate: f.endDate ?? "",
      }));
    onChange({ dateFilters: mapped });
  };

  const handleAddRow = () => {
    const cloned: Array<{
      attribute: string;
      operator: Operator | "";
      startDate: string;
      endDate: string;
      date: string;
    }> = [
      ...dateFilters,
      {
        attribute: "",
        operator: "" as Operator | "",
        date: "",
        startDate: "",
        endDate: "",
      },
    ];
    setDateFilters(cloned);
    updateParentState(cloned);
  };

  const handleRemoveRow = (index: number) => {
    const cloned = dateFilters.filter((_, i) => i !== index);
    const fallback: Array<{
      attribute: string;
      operator: Operator | "";
      startDate: string;
      endDate: string;
      date: string;
    }> = [
      {
        attribute: "",
        operator: "" as Operator | "",
        date: "",
        startDate: "",
        endDate: "",
      },
    ];
    const result = cloned.length ? cloned : fallback;
    setDateFilters(result);
    updateParentState(result);
  };

  const usedAttributes = dateFilters
    .map((f) => f.attribute)
    .filter(Boolean) as string[];

  return (
    <Accordion
      elevation={0}
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        borderRadius: "12px",
        backgroundColor: "white",
        border: "1px solid #E5E7EB",
        boxShadow: "none",
        "&:before": { display: "none" }, // remove default divider line
        "&:not(:last-of-type)": {
          borderBottom: "1px solid #E5E7EB",
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 2.5,
          py: 1.5,
          minHeight: 64,
          "& .MuiAccordionSummary-content": {
            alignItems: "center",
            margin: 0,
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AssignmentOutlinedIcon sx={{ color: "#7C3AED", fontSize: 24 }} />
          <Typography fontSize={18} fontWeight={500}>
            Assignment filters
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 3, pb: 3 }}>
        <Box sx={{ borderTop: "1px solid #E5E7EB", pt: 2 }}>
          <Stack spacing={1} pb={2}>
            <Typography variant="subtitle2">
              Assignment
            </Typography>

            <Autocomplete<DdlListResult, true, false, false>
              id="assignment-filter-assignments-autocomplete"
              multiple
              options={assignmentsOptions?.result || []}
              renderOption={(props, option) => (
                <li {...props}>
                  <span
                    style={{
                      color:
                        option.status === "Inactive" ? gray[400] : "inherit",
                    }}
                  >
                    {option.value}
                  </span>
                </li>
              )}
              value={(assignmentsOptions?.result || []).filter((opt) =>
                value.assignments.includes(opt.key),
              )}
              size="small"
              onChange={(_, newValue) =>
                onChange({ assignments: newValue.map((opt) => opt.key) })
              }
              getOptionLabel={(option) => option.value || ""}
              isOptionEqualToValue={(option, val) => option.key === val.key}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select assignments"
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: 44,
                    },
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.key}
                    label={option.value}
                    sx={{
                      backgroundColor: "white",
                      padding: "14px 6px",
                      borderRadius: "6px",
                    }}
                  />
                ))
              }
              slotProps={{
                popupIndicator: {
                  sx: { border: "none" },
                },
                clearIndicator: {
                  sx: { display: "none" },
                },
              }}
            />
          </Stack>

          <Stack spacing={1} pb={2}>
            <Typography variant="subtitle2">
              User assignment status
            </Typography>

            <Autocomplete<DdlListResult, true, false, false>
              id="assignment-filter-status-autocomplete"
              multiple
              options={assignmentsStatusOptions?.result || []}
              value={(assignmentsStatusOptions?.result || []).filter((opt) =>
                value.assignmentsStatus.includes(opt.key),
              )}
              size="small"
              onChange={(_, newValue) =>
                onChange({ assignmentsStatus: newValue.map((opt) => opt.key) })
              }
              getOptionLabel={(option) => option.value || ""}
              isOptionEqualToValue={(option, val) => option.key === val.key}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select user assignment status"
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: 44,
                    },
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.key}
                    label={option.value}
                    sx={{
                      backgroundColor: "white",
                      padding: "14px 6px",
                      borderRadius: "6px",
                    }}
                  />
                ))
              }
              slotProps={{
                popupIndicator: { sx: { border: "none" } },
                clearIndicator: { sx: { display: "none" } },
              }}
            />
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2">
              Progress status
            </Typography>

            <Autocomplete<DdlListResult, true, false, false>
              id="assignment-filter-progress-status-autocomplete"
              multiple
              options={progressStatusOptions?.result || []}
              value={(progressStatusOptions?.result || []).filter((opt) =>
                value.progressStatus.includes(opt.key),
              )}
              size="small"
              onChange={(_, selected) =>
                onChange({ progressStatus: selected.map((o) => o.key) })
              }
              getOptionLabel={(option) => option.value}
              isOptionEqualToValue={(option, val) => option === val}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select progress status"
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: 44,
                    },
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.key}
                    label={option.value}
                    sx={{
                      backgroundColor: "white",
                      padding: "14px 6px",
                      borderRadius: "6px",
                    }}
                  />
                ))
              }
              slotProps={{
                popupIndicator: { sx: { border: "none" } },
                clearIndicator: { sx: { display: "none" } },
              }}
            />
          </Stack>

          <Stack spacing={1.5} mt={3}>
            <Typography variant="subtitle2">Date filters</Typography>

            <Box
              sx={{
                border: "2px solid #EAECF0",
                borderRadius: "12px",
                p: 2,
              }}
            >
              <Stack spacing={2}>
                {dateFilters.map((filter, index) => (
                  <DateFilterField
                    key={index}
                    index={index}
                    usedAttributes={usedAttributes}
                    attribute={filter.attribute}
                    operator={filter.operator}
                    startDate={filter.startDate}
                    endDate={filter.endDate}
                    date={filter.date}
                    onAttributeChange={(val: any) =>
                      handleAttributeChange(index, val)
                    }
                    onOperatorChange={(val) =>
                      handleOperatorChange(index, val as Operator | "")
                    }
                    onDateChange={(start: any, end: any, singleDate: any) =>
                      handleDateChange(index, start, end, singleDate)
                    }
                    onRemoveRow={handleRemoveRow}
                  />
                ))}
              </Stack>
              <Button
                variant="outlined"
                startIcon={<AddOutlinedIcon />}
                onClick={handleAddRow}
                sx={{
                  mt: 2,
                  borderRadius: "8px",
                  textTransform: "none",
                  color: "#344054",
                  borderColor: "#D0D5DD",
                  "&:hover": { borderColor: "#98A2B3", bgcolor: "transparent" },
                }}
              >
                Add filter
              </Button>
            </Box>
          </Stack>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
